import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import MainLayout from "@/components/layouts";
import { useIndividualAppointmentUpdate } from "./update.hooks";
import AppointmentStatusBadge from "@/app/pmc/appointments/components/AppointmentStatusBadge";
import { TaskChecklist } from "@/components/shared/TaskChecklist";
import { PhotoUploadSection } from "@/components/shared/PhotoUploadSection";
import { GuidedPhotoWorkflow, GuidedWorkflowMode } from "@/components/shared/GuidedPhotoWorkflow";
import { appointment_status, AppointmentClockData, getExtraPhotos } from "@/models/appointment.model";
import {
  allBeforePhotosUploaded,
  getChecklistLockMessage,
  getMissingWorkflows,
  WORKFLOWS_NOT_LOADED_MESSAGE,
} from "@/lib/utils/workflow-photo-gate";
import { Button } from "@/components/ui/button";
import { Play, Square, CheckCircle2, MessageCircle, Lock, Copy, Check, Camera } from "lucide-react";
import AppointmentCommentsToggle from "@/components/shared/AppointmentCommentsToggle";
import { ClockSummary } from "@/components/shared/clock-summary";
import { PreviousWorkSection } from "@/components/shared/PreviousWorkSection";
import { AppointmentChatService } from "@/services/appointment-chat.service";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import moment from "moment";
import { FINISHED_OR_COMPLETED } from "@/repositories/appointment-records";

export default function IndividualAppointmentUpdatePage() {
  const { appointmentId: id = "" } = useParams();
  const navigate = useNavigate();
  const {
    loading,
    appointment,
    serviceTasks,
    serviceWorkflows,
    appointmentTasks,
    appointmentWorkflows,
    checklistLoaded,
    workflowsLoaded,
    checklistError,
    retryChecklist,
    checklistProgress,
    checklistGateError,
    updateTaskStatus,
    uploadPhoto,
    completeClockIn,
    startWork,
    addExtraPhoto,
    removeExtraPhoto,
    completeClockOut,
  } = useIndividualAppointmentUpdate(id);

  const [guidedWorkflowOpen, setGuidedWorkflowOpen] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [guidedWorkflowMode, setGuidedWorkflowMode] = useState<GuidedWorkflowMode>("clockin");
  const [autoReopenChecked, setAutoReopenChecked] = useState(false);
  const [copied, setCopied] = useState(false);

  const clockInData = appointment?.meta_data?.clock_in as AppointmentClockData | undefined;
  const clockOutData = appointment?.meta_data?.clock_out as AppointmentClockData | undefined;

  const workStarted = Boolean(appointment?.meta_data?.work_started_at);

  // Auto-reopen guided flow on page load if incomplete
  useEffect(() => {
    if (loading || !appointment || autoReopenChecked || serviceWorkflows.length === 0) return;
    setAutoReopenChecked(true);

    // If clocked in (but work not yet started) and some before photos are
    // missing, resume the clock-in flow's before tour
    if (clockInData?.time && !clockOutData?.time && !workStarted) {
      const allBeforeDone = serviceWorkflows.every((wf) =>
        appointmentWorkflows.some((aw) => aw.workflow === wf.id && !aw.is_after)
      );
      if (!allBeforeDone) {
        setGuidedWorkflowMode("clockin");
        setGuidedWorkflowOpen(true);
        return;
      }
    }
  }, [loading, appointment, serviceWorkflows, appointmentWorkflows, autoReopenChecked, workStarted]);

  const handleClockIn = () => {
    if (clockInData?.time) return; // already clocked in
    setGuidedWorkflowMode("clockin");
    setGuidedWorkflowOpen(true);
  };

  // "Begin Tasks" closes the before-photo review: the hook validates the
  // before set is complete, locks it, and unlocks the checklist.
  const handleStartWork = () => {
    if (!clockInData?.time || workStarted) return;
    void startWork();
  };

  const handleAfterTour = () => {
    if (!workStarted) return;
    if (!workflowsLoaded) {
      toast.error(WORKFLOWS_NOT_LOADED_MESSAGE);
      return;
    }
    if (checklistGateError) {
      toast.error(checklistGateError);
      return;
    }
    setGuidedWorkflowMode("aftertour");
    setGuidedWorkflowOpen(true);
  };

  // "Complete Work": routes to the earliest unmet step, then asks for the
  // clock-out front-door photo which commits the completion.
  const handleCompleteWork = () => {
    if (!clockInData?.time || clockOutData?.time) return;
    // Fail closed until the photo requirements load — the checks below all
    // pass vacuously against an empty serviceWorkflows from a failed fetch.
    if (!workflowsLoaded) {
      toast.error(WORKFLOWS_NOT_LOADED_MESSAGE);
      return;
    }
    if (!allBeforePhotosUploaded(serviceWorkflows, appointmentWorkflows)) {
      toast.error("Upload all before photos before completing work.");
      setGuidedWorkflowMode("clockin");
      setGuidedWorkflowOpen(true);
      return;
    }
    if (!workStarted) {
      toast.error("Tap Begin Tasks to lock before photos and unlock the checklist.");
      return;
    }
    if (checklistGateError) {
      toast.error(checklistGateError);
      return;
    }
    if (getMissingWorkflows(serviceWorkflows, appointmentWorkflows, true).length > 0) {
      toast.error("Take the after photos before completing work.");
      setGuidedWorkflowMode("aftertour");
      setGuidedWorkflowOpen(true);
      return;
    }
    setGuidedWorkflowMode("clockout");
    setGuidedWorkflowOpen(true);
  };

  const handleGuidedComplete = async (clockPhotoUrl: string) => {
    try {
      if (guidedWorkflowMode === "clockin") {
        await completeClockIn(clockPhotoUrl);
      } else if (guidedWorkflowMode === "aftertour") {
        // Photos are already uploaded by the tour; nothing to commit. The
        // worker reviews them on the page, then taps Complete Work.
        toast.success("After photos saved. Review them, then tap Complete Work when ready.");
      } else {
        await completeClockOut(clockPhotoUrl);
      }
      setGuidedWorkflowOpen(false);
    } catch (err) {
      console.error("Guided workflow did not complete:", err);
    }
  };

  const handleOpenChat = async () => {
    if (!appointment?.id) return;
    setLoadingChat(true);
    try {
      const chatRoom = await AppointmentChatService.getAppointmentGroupChat(appointment.id);
      if (chatRoom) {
        navigate(`/messages/${chatRoom.id}`);
      } else {
        toast.error("Chat room not found for this appointment");
      }
    } catch (error) {
      console.error("Failed to open chat:", error);
      toast.error("Failed to open chat");
    } finally {
      setLoadingChat(false);
    }
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(id);
    setCopied(true);
    toast.success("Appointment ID copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const breadcrumb = [
    { name: "Appointments", href: "/appointments", current: false },
    { name: `Booking #${id.slice(0, 8)}`, href: "#", current: true },
  ];

  if (loading) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center gap-4 col-span-full">
                  <Skeleton className="w-16 h-16 rounded-md flex-shrink-0" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-4 w-52" />
                    <Skeleton className="h-4 w-44" />
                  </div>
                </div>
                <div className="col-span-full grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-5 w-28" />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-36" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  if (!appointment) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <p className="text-destructive">Appointment not found</p>
      </MainLayout>
    );
  }

  const appt = appointment;
  const isClockedIn = !!clockInData?.time;
  const beforePhotosComplete = allBeforePhotosUploaded(serviceWorkflows, appointmentWorkflows);
  const afterPhotosComplete = getMissingWorkflows(serviceWorkflows, appointmentWorkflows, true).length === 0;
  const checklistLockMessage = getChecklistLockMessage(appt.status, beforePhotosComplete, workStarted);
  const isLocked = [appointment_status.done, appointment_status.cancelled, appointment_status.completed].includes(appt.status as any);
  const canClockIn = appt.status === appointment_status.accepted || appt.status === appointment_status.goback;
  const isInProgress = appt.status === appointment_status.inprogress;
  const checklistComplete = checklistLoaded && checklistProgress.allComplete;
  // The primary CTA for the started-work phase: after tour once the checklist
  // is done and after photos remain, otherwise Complete Work (which routes to
  // the earliest unmet step when tapped early).
  const showAfterTourCta = isInProgress && workStarted && checklistComplete && !afterPhotosComplete;
  // beforePhotosComplete is vacuously true until the workflows load, so the
  // Begin Tasks button also waits on workflowsLoaded (fail closed).
  const startWorkDisabledTitle = !workflowsLoaded
    ? WORKFLOWS_NOT_LOADED_MESSAGE
    : !beforePhotosComplete
      ? "Upload all before photos first"
      : undefined;
  const extraPhotos = getExtraPhotos(appt.meta_data, false).concat(getExtraPhotos(appt.meta_data, true));
  const doneAt = (appt.meta_data as any)?.done_at as string | undefined;
  const gobackAt = (appt.meta_data as any)?.goback_at as string | undefined;
  const previousWork = (appt.meta_data as any)?.previous_work as import("@/models/appointment.model").PreviousWorkSnapshot[] | undefined;

  return (
    <MainLayout
      breadcrumb={breadcrumb}
      pageHeader={{
        title: `Booking #${id.slice(0, 8)}`,
        subtitle: "Appointment details and documentation",
        additionalButtons: (
          <div className="hidden sm:flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleCopyId} className="gap-1.5">
              {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy ID"}
            </Button>
            <Button variant="outline" size="sm" onClick={handleOpenChat} disabled={loadingChat}>
              <MessageCircle className="h-4 w-4 mr-2" />
              {loadingChat ? "Loading..." : "Chat"}
            </Button>
            {canClockIn ? (
              <Button onClick={handleClockIn}>
                <Camera className="w-4 h-4 mr-2" />
                Start Work
              </Button>
            ) : isInProgress && !workStarted ? (
              <Button onClick={handleStartWork} disabled={!!startWorkDisabledTitle} title={startWorkDisabledTitle}>
                <Play className="w-4 h-4 mr-2" />
                Begin Tasks
              </Button>
            ) : showAfterTourCta ? (
              <Button onClick={handleAfterTour}>
                <Camera className="w-4 h-4 mr-2" />
                After Photo Tour
              </Button>
            ) : isInProgress ? (
              <Button variant="destructive" onClick={handleCompleteWork}>
                <Square className="w-4 h-4 mr-2" />
                Complete Work
              </Button>
            ) : isLocked ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                Work completed
              </div>
            ) : null}
          </div>
        ),
      }}
    >
      <div className="container mx-auto pb-20 sm:pb-6 space-y-6">
        {/* Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="uppercase">Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Row 1: Property */}
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16 rounded-md flex-shrink-0">
                <AvatarImage
                  src={
                    appt.property?.image ??
                    "https://app.shynenetwork.com/assets/signupAsset/images/apartment_default.jpg"
                  }
                  alt={appt.property.name}
                />
                <AvatarFallback className="rounded-md">{appt.property.name[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold">{appt.property.name}</h2>
                <p className="text-sm text-muted-foreground">{appt.property?.address ?? "No address provided"}</p>
                <p className="text-sm text-muted-foreground">
                  Unit: {appt.property.unit_number} | Beds: {appt.property.beds} | Baths: {appt.property.baths}
                </p>
              </div>
            </div>

            {/* Row 2: Appointment ID - Status */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Info label="Appointment ID">
                <span className="flex items-center gap-1.5">
                  {appt.id.slice(0, 8)}
                  <button
                    onClick={handleCopyId}
                    className="text-muted-foreground hover:text-primary transition-colors sm:hidden"
                  >
                    {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </span>
              </Info>
              <Info label="Status">
                <AppointmentStatusBadge status={appt.status} />
              </Info>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Info label="Service">
                {appt.service.name}
                <br />
                <span className="text-muted-foreground text-sm">{appt.service.parent_name}</span>
              </Info>
              {appt.individual_payment != null && <Info label="Pay">${appt.individual_payment.toFixed(2)}</Info>}
              <Info label="Vendor">{appt.vendor.name}</Info>
            </div>

            {/* Row 4: Scheduled Date - Created Date - Done At / Go Back At */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Info label="Scheduled Date">{moment(appt.date).format("MM/DD/YYYY, h:mm a")}</Info>
              <Info label="Created Date">{moment(appt.created_at).format("MM/DD/YYYY")}</Info>
              {FINISHED_OR_COMPLETED.includes(appt.status) && doneAt && (
                <Info label="Done At">{moment(doneAt).format("MM/DD/YYYY, h:mm a")}</Info>
              )}
              {appt.status === appointment_status.goback && gobackAt && (
                <Info label="Go Back At">{moment(gobackAt).format("MM/DD/YYYY, h:mm a")}</Info>
              )}
            </div>

            {/* Row 5: Work Order Notes */}
            {appt.work_order_notes && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-1">Work Order Notes</h3>
                <p className="text-base font-medium">{appt.work_order_notes}</p>
              </div>
            )}

            <AppointmentCommentsToggle appointmentId={appt.id} userRole="individual" />

            {/* Cancellation Details */}
            {appt.status === appointment_status.cancelled && appt.meta_data && (
              <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md p-4 space-y-2">
                <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 uppercase">Cancellation Details</h3>
                {(appt.meta_data as any).cancellation_notes && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Reason</p>
                    <p className="text-sm text-red-900 dark:text-red-100">
                      {(appt.meta_data as any).cancellation_notes}
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-4">
                  {appt.cancelled_by_user?.name && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Cancelled By</p>
                      <p className="text-sm text-red-900 dark:text-red-100">{appt.cancelled_by_user.name}</p>
                    </div>
                  )}
                  {(appt.meta_data as any).cancelled_at && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Cancelled On</p>
                      <p className="text-sm text-red-900 dark:text-red-100">
                        {moment((appt.meta_data as any).cancelled_at).format("MM/DD/YYYY, h:mm a")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Clock Summary */}
        {isClockedIn && <ClockSummary clockIn={clockInData} clockOut={clockOutData} />}

        {/* Previous Work (shown when appointment has been through goback) */}
        {previousWork && <PreviousWorkSection previousWork={previousWork} />}

        {/* Tasks Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="uppercase">Service Tasks</CardTitle>
              {isLocked && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm">Read Only</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {checklistError ? (
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <p className="text-sm text-muted-foreground">
                  Couldn't load the service tasks or photo requirements. Completing work stays locked until they load.
                </p>
                <Button variant="outline" size="sm" onClick={retryChecklist}>
                  Retry
                </Button>
              </div>
            ) : !checklistLoaded ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Loading service tasks…</p>
            ) : (
              <TaskChecklist
                serviceTasks={serviceTasks}
                appointmentTasks={appointmentTasks}
                onTaskToggle={isLocked ? () => {} : updateTaskStatus}
                readOnly={isLocked}
                disabled={checklistLockMessage !== null && !isLocked}
                disabledMessage={checklistLockMessage}
              />
            )}
          </CardContent>
        </Card>

        {/* Photos Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="uppercase">Photo Documentation</CardTitle>
              {isLocked && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Lock className="w-4 h-4" />
                  <span className="text-sm">Read Only</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <PhotoUploadSection
              serviceWorkflows={serviceWorkflows}
              appointmentWorkflows={appointmentWorkflows}
              onPhotoUpload={isLocked ? () => {} : uploadPhoto}
              readOnly={isLocked}
              status={appt.status}
              workStarted={workStarted}
              checklistComplete={checklistProgress.allComplete}
              checklistLoaded={checklistLoaded}
              extraPhotos={extraPhotos}
              onAddExtraPhoto={isLocked ? undefined : addExtraPhoto}
              onRemoveExtraPhoto={isLocked ? undefined : removeExtraPhoto}
            />
          </CardContent>
        </Card>
      </div>

      {/* Sticky Footer — Mobile only */}
      {canClockIn && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur-sm shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
          <div className="px-4 py-3 flex items-center justify-center">
            <Button size="lg" className="w-full" onClick={handleClockIn}>
              <Camera className="w-4 h-4 mr-2" />
              Start Work
            </Button>
          </div>
        </div>
      )}
      {isInProgress && !workStarted && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur-sm shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
          <div className="px-4 py-3 flex items-center justify-center">
            <Button
              size="lg"
              className="w-full"
              onClick={handleStartWork}
              disabled={!!startWorkDisabledTitle}
              title={startWorkDisabledTitle}
            >
              <Play className="w-4 h-4 mr-2" />
              Begin Tasks
            </Button>
          </div>
        </div>
      )}
      {isInProgress && workStarted && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur-sm shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
          <div className="px-4 py-3 flex items-center justify-center">
            {showAfterTourCta ? (
              <Button size="lg" className="w-full" onClick={handleAfterTour}>
                <Camera className="w-4 h-4 mr-2" />
                After Photo Tour
              </Button>
            ) : (
              <Button size="lg" variant="destructive" className="w-full" onClick={handleCompleteWork}>
                <Square className="w-4 h-4 mr-2" />
                Complete Work
              </Button>
            )}
          </div>
        </div>
      )}
      {isLocked && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur-sm">
          <div className="px-4 py-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            Work completed
          </div>
        </div>
      )}

      {/* Guided Photo Workflow Modal */}
      <GuidedPhotoWorkflow
        open={guidedWorkflowOpen}
        onClose={() => setGuidedWorkflowOpen(false)}
        mode={guidedWorkflowMode}
        appointmentId={id}
        serviceWorkflows={serviceWorkflows}
        appointmentWorkflows={appointmentWorkflows}
        clockInData={clockInData}
        clockOutData={clockOutData}
        onPhotoUpload={uploadPhoto}
        onComplete={handleGuidedComplete}
      />
    </MainLayout>
  );
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-1">{label}</h3>
      <p className="text-base font-medium leading-snug">{children}</p>
    </div>
  );
}
